import React, { useEffect, useRef, useState } from "react";
import { Grid, Tooltip } from "@mui/material";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RenderChats } from "../RenderChats";
import { DrawInternalNotesInterface } from "../../stores/drawRequest/drawRequest.interface";
import { RootState } from "../../stores/rootReducer";
import iconMinimize from "../../images/icon_minimize.svg";
import iconMaximize from "../../images/icon_maximize.svg";

import { ChatTextArea, useNotesStyles } from "../ChatTextArea";
import { getConfig } from "../../config/config";
import { getStageBgColor } from "./Constant";
import { MMMDDYYYYDateFormat } from "../../utils/utils";
import { isAMTView } from "../AssetManagement.utils";
import { isOrigUser } from "../../utils/AccessManagement";

export const DrawDiscussionChat = ({
  drawInternalChats,
  drawDiscussionsHistory,
  chatItem,
  handleTextChange,
  handleChatsPost,
  disableChat,
  btnLabel,
  styleProps
}: {
  drawInternalChats: DrawInternalNotesInterface[];
  drawDiscussionsHistory?: any;
  chatItem: any;
  handleTextChange: (e: any) => void;
  handleChatsPost: () => void;
  disableChat?: boolean;
  btnLabel?: string;
  styleProps?: any;
}) => {
  const { drawId } = useParams<any>() as any;
  const drawHistory = useSelector(
    (state: RootState) => state.drawRequestStore.drawHistory
  );
  const classesNotes = useNotesStyles();

  const getDiscussionDrawStage = (drawIdHistory: string) => {
    const stage = drawDiscussionsHistory?.[drawIdHistory]?.drawDetails?.stage?.trim();
    if (stage) {
      return stage;
    }
    const historyRow = drawHistory?.find(
      (row: { drawId?: string | number; drawStatus?: string; stage?: string }) =>
        String(row?.drawId) === String(drawIdHistory)
    );
    return historyRow?.drawStatus?.trim() || historyRow?.stage?.trim() || "";
  };
  const addChatsRef = useRef<HTMLDivElement>(null);
  const [currentChatId, setCurrentChatId] = useState<string>("");
  const [chantsToRender, setChantsToRender] = useState<any>({});
  const [orderedDraws, setOrderedDraws] = useState<any>([]);

  const toNumeric = (val: any): number => {
    if (val === null || val === undefined || String(val).trim() === "") {
      return Number.NaN;
    }
    const n = Number(String(val).replace(/[^0-9.-]/g, ""));
    return Number.isNaN(n) ? Number.NaN : n;
  };

  const getSortKey = (id: string, source: any) => {
    const drawNumber = toNumeric(source?.[id]?.drawDetails?.drawNumber);
    const requestedDate = source?.[id]?.drawDetails?.requestedDate
      ? new Date(source[id].drawDetails.requestedDate).getTime()
      : Number.NaN;
    return {
      drawNumber,
      requestedDate
    };
  };

  useEffect(() => {
    if (drawDiscussionsHistory) {
      let updateDiscussionHistory = drawDiscussionsHistory;
      if (
        !isAMTView() &&
        Object.keys(updateDiscussionHistory || {}).length > 0
      ) {
        updateDiscussionHistory = {
          [drawId]: updateDiscussionHistory[drawId]
        };
      }
      const keys = Object.keys(updateDiscussionHistory) || {};
      setChantsToRender(updateDiscussionHistory);
      keys.sort((a: string, b: string) => {
        const aKey = getSortKey(a, updateDiscussionHistory);
        const bKey = getSortKey(b, updateDiscussionHistory);
        if (!Number.isNaN(aKey.drawNumber) || !Number.isNaN(bKey.drawNumber)) {
          if (Number.isNaN(aKey.drawNumber)) return 1;
          if (Number.isNaN(bKey.drawNumber)) return -1;
          return bKey.drawNumber - aKey.drawNumber;
        }
        if (!Number.isNaN(aKey.requestedDate) || !Number.isNaN(bKey.requestedDate)) {
          if (Number.isNaN(aKey.requestedDate)) return 1;
          if (Number.isNaN(bKey.requestedDate)) return -1;
          return bKey.requestedDate - aKey.requestedDate;
        }
        return String(b).localeCompare(String(a));
      });
      setOrderedDraws(keys);
    } else {
      setChantsToRender({ [drawId]: drawInternalChats });
    }
  }, [drawDiscussionsHistory, drawInternalChats, drawId]);

  function isMinimized(drawIdHistory: string) {
    return drawIdHistory === currentChatId;
  }

  useEffect(() => {
    setCurrentChatId(drawId);
  }, [drawId]);

  function openInNewTab(id: string) {
    if (!drawDiscussionsHistory) return;
    const urlPath = `drawDetails/${id}`;
    window.open(`${getConfig().redirectUrl}${urlPath}`);
  }

  return (
    <div id="myForm" style={{ minWidth: "100%" }}>
      <div className={classesNotes.internalNotesTab} style={styleProps}>
        {(drawDiscussionsHistory
          ? orderedDraws
          : Object.keys(chantsToRender)
        ).map(
          (drawIdHistory: string) =>
            chantsToRender[drawIdHistory]?.drawDetails?.stage !== "Draft" && (
              <React.Fragment key={drawIdHistory}>
                <Grid
                  item
                  xs={12}
                  container
                  data-testid="discussion-draw-header"
                  className={
                    isMinimized(drawIdHistory)
                      ? classesNotes.discussionDrawHeaderMin
                      : classesNotes.discussionDrawHeaderMax
                  }
                >
                  {drawDiscussionsHistory && isAMTView() && (
                    <>
                      <Grid item xs={9} sm={11}>
                        <span
                          className={classesNotes.drawIdHistory}
                          onClick={() => openInNewTab(drawIdHistory)}
                        >
                          {drawIdHistory}
                        </span>
                        {drawIdHistory === drawId && (
                          <span
                            className={classesNotes.currentDrawTag}
                            data-testid="current-draw-tag"
                            style={{ backgroundColor: "rgb(63 214 142)" }}
                          >
                            CURRENT DRAW
                          </span>
                        )}
                        {drawIdHistory !== drawId && (() => {
                          const stageLabel = getDiscussionDrawStage(drawIdHistory);
                          if (!stageLabel) {
                            return null;
                          }
                          return (
                            <span
                              className={classesNotes.currentDrawTag}
                              data-testid="draw-stage-tag"
                              style={{
                                backgroundColor: getStageBgColor(stageLabel)
                              }}
                            >
                              {stageLabel}
                            </span>
                          );
                        })()}
                        {drawDiscussionsHistory[drawIdHistory]?.drawDetails
                          ?.requestedDate && (
                          <span
                            className={classesNotes.currentDrawTag}
                            data-testid="current-draw-tag"
                            style={{
                              color: "black",
                              backgroundColor: "white"
                            }}
                          >
                            {MMMDDYYYYDateFormat(
                              drawDiscussionsHistory[drawIdHistory]?.drawDetails
                                ?.requestedDate
                            )}
                          </span>
                        )}
                      </Grid>
                      <Grid item xs={3} sm={1}>
                        <Tooltip
                          title={
                            isMinimized(drawIdHistory) ? "Expand" : "Collapse"
                          }
                        >
                          <img
                            // className={classes.headingImage}
                            src={
                              isMinimized(drawIdHistory)
                                ? iconMinimize
                                : iconMaximize
                            }
                            alt={
                              isMinimized(drawIdHistory)
                                ? "Minimize section"
                                : "Maximize section"
                            }
                            onClick={() => {
                              isMinimized(drawIdHistory)
                                ? setCurrentChatId("")
                                : setCurrentChatId(drawIdHistory);
                            }}
                            onKeyDown={() => {
                              isMinimized(drawIdHistory)
                                ? setCurrentChatId("")
                                : setCurrentChatId(drawIdHistory);
                            }}
                          />
                        </Tooltip>
                      </Grid>
                    </>
                  )}
                </Grid>
                {(isMinimized(drawIdHistory) || !isAMTView()) && (
                  <Grid
                    style={{
                      padding: drawDiscussionsHistory ? "2px 5px 10px" : "0px"
                    }}
                  >
                    <RenderChats
                      Chats={
                        drawDiscussionsHistory
                          ? chantsToRender[drawIdHistory]?.discussions || []
                          : chantsToRender[drawIdHistory]
                      }
                    />
                    {drawIdHistory === drawId &&
                      (isAMTView() || isOrigUser()) && (
                        <div ref={addChatsRef}>
                          <ChatTextArea
                            textValue={chatItem.text}
                            handleTextChange={handleTextChange}
                            handleSubmitClicked={handleChatsPost}
                            btnLabel={btnLabel}
                            disableChat={disableChat}
                          />
                        </div>
                      )}
                  </Grid>
                )}
              </React.Fragment>
            )
        )}
      </div>
    </div>
  );
};
